import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useApplications } from '../context/ApplicationContext';
import { useWorkflow } from '../context/WorkflowContext';
import UserLayout from '../components/UserLayout';
import Card, { CardHeader, CardBody } from '../components/Card';
import Button from '../components/Button';
import ReferenceNumberSearch from '../components/ReferenceNumberSearch';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, ArrowRightIcon, CalendarIcon, BuildingIcon, UserIcon, CreditCardIcon, MessageSquareIcon, FileIcon, CheckIcon, DownloadIcon, UploadIcon, PaperclipIcon, SendIcon } from 'lucide-react';
const TrackApplication: React.FC = () => {
  const {
    referenceNumber
  } = useParams<{
    referenceNumber: string;
  }>();
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get('query');
  const {
    getApplicationByReferenceNumber,
    addCommentToApplication,
    uploadDocumentToApplication
  } = useApplications();
  const {
    getWorkflowInstanceByReferenceNumber
  } = useWorkflow();
  const [application, setApplication] = useState<any>(null);
  const [workflowInstance, setWorkflowInstance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [fileUpload, setFileUpload] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    const fetchApplicationData = () => {
      setLoading(true);
      setError(null);
      // Use either the path parameter or query parameter
      const refNumber = referenceNumber || queryParam;
      if (!refNumber) {
        setLoading(false);
        return;
      }
      try {
        // Log the reference number being searched for debugging
        console.log('Searching for application with reference number:', refNumber);
        const app = getApplicationByReferenceNumber(refNumber);
        if (app) {
          console.log('Application found:', app);
          setApplication(app);
          // Get workflow instance if it exists
          const workflow = getWorkflowInstanceByReferenceNumber(refNumber);
          setWorkflowInstance(workflow);
        } else {
          console.log('Application not found for reference number:', refNumber);
          setError(`Application with reference number ${refNumber} not found`);
        }
      } catch (err) {
        console.error('Error fetching application:', err);
        setError('An error occurred while fetching the application data');
      } finally {
        setLoading(false);
      }
    };
    fetchApplicationData();
  }, [referenceNumber, queryParam, getApplicationByReferenceNumber, getWorkflowInstanceByReferenceNumber]);
  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim() || !application) return;
    setSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      addCommentToApplication(application.id, comment, application.userId,
      // In a real app, this would be the current user's ID
      application.userName);
      // Refresh application data
      const updatedApp = getApplicationByReferenceNumber(application.referenceNumber);
      setApplication(updatedApp);
      setComment('');
      setSubmitting(false);
    }, 500);
  };
  const handleFileUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fileUpload || !uploadType || !application) return;
    setSubmitting(true);
    // Simulate file upload
    setTimeout(() => {
      // Create a fake URL for the demo
      const fakeUrl = `https://storage.edostate.gov.ng/applications/${application.referenceNumber}/${fileUpload.name}`;
      uploadDocumentToApplication(application.id, {
        name: fileUpload.name,
        url: fakeUrl,
        uploadedBy: application.userId,
        type: uploadType,
        size: fileUpload.size
      });
      // Refresh application data
      const updatedApp = getApplicationByReferenceNumber(application.referenceNumber);
      setApplication(updatedApp);
      setFileUpload(null);
      setUploadType('');
      setSubmitting(false);
    }, 1000);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-6550">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-6551" />
            Approved
          </span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-6552">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-6553" />
            Rejected
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-6554">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-6555" />
            Pending
          </span>;
      case 'processing':
      case 'under_review':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-6556">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-6557" />
            Processing
          </span>;
      case 'awaiting_payment':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-6558">
            <CreditCardIcon className="w-3 h-3 mr-1" data-id="element-6559" />
            Awaiting Payment
          </span>;
      case 'awaiting_documents':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-6560">
            <FileTextIcon className="w-3 h-3 mr-1" data-id="element-6561" />
            Awaiting Documents
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-6562">
            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-6563" />
            {status.replace('_', ' ').toUpperCase()}
          </span>;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <UserLayout data-id="element-6564">
      <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen" data-id="element-6565">
        <div className="max-w-7xl mx-auto" data-id="element-6566">
          <div className="mb-6" data-id="element-6567">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-6568">
              Track Application
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-6569">
              Enter your application reference number to track its status.
            </p>
          </div>

          {!referenceNumber && !queryParam && <Card className="mb-8" data-id="element-6570">
              <CardBody className="p-6" data-id="element-6571">
                <ReferenceNumberSearch redirectToTrackingPage={true} data-id="element-6572" />
              </CardBody>
            </Card>}

          {loading ? <div className="flex justify-center items-center p-12" data-id="element-6573">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700" data-id="element-6574"></div>
            </div> : error ? <Card className="border-red-200" data-id="element-6575">
              <CardBody className="p-6" data-id="element-6576">
                <div className="flex items-center justify-center text-red-600" data-id="element-6577">
                  <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-6578" />
                  <p data-id="element-6579">{error}</p>
                </div>
                <div className="mt-4 flex justify-center" data-id="element-6580">
                  <Button variant="outline" onClick={() => setError(null)} data-id="element-6581">
                    Try Another Reference Number
                  </Button>
                </div>
              </CardBody>
            </Card> : application ? <div className="space-y-6" data-id="element-6582">
              {/* Application Header */}
              <Card data-id="element-6583">
                <CardBody className="p-6" data-id="element-6584">
                  <div className="flex flex-col md:flex-row md:items-center justify-between" data-id="element-6585">
                    <div data-id="element-6586">
                      <div className="flex items-center" data-id="element-6587">
                        <FileTextIcon className="h-5 w-5 text-green-600 mr-2" data-id="element-6588" />
                        <h2 className="text-xl font-semibold text-gray-900" data-id="element-6589">
                          {application.serviceName}
                        </h2>
                      </div>
                      <p className="text-gray-500 mt-1" data-id="element-6590">
                        Reference Number:{' '}
                        <span className="font-medium" data-id="element-6591">
                          {application.referenceNumber}
                        </span>
                      </p>
                    </div>
                    <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end" data-id="element-6592">
                      {getStatusBadge(application.status)}
                      <p className="text-sm text-gray-500 mt-1" data-id="element-6593">
                        Submitted on {formatDate(application.submittedAt)}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Application Status and Workflow */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-6594">
                <div className="md:col-span-2" data-id="element-6595">
                  <Card data-id="element-6596">
                    <CardHeader className="bg-gray-50" data-id="element-6597">
                      <h3 className="text-lg font-medium text-gray-900" data-id="element-6598">
                        Approval Process
                      </h3>
                    </CardHeader>
                    <CardBody className="p-6" data-id="element-6599">
                      {workflowInstance ? <div data-id="element-6600">
                          <div className="mb-4" data-id="element-6601">
                            <div className="flex items-center justify-between mb-2" data-id="element-6602">
                              <span className="text-sm font-medium text-gray-700" data-id="element-6603">
                                Overall Progress
                              </span>
                              <span className="text-sm text-gray-500" data-id="element-6604">
                                Step {workflowInstance.currentStepIndex + 1} of{' '}
                                {workflowInstance.steps.length}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-6605">
                              <div className="bg-green-600 h-2.5 rounded-full" style={{
                          width: `${(workflowInstance.currentStepIndex + 1) / workflowInstance.steps.length * 100}%`
                        }} data-id="element-6606"></div>
                            </div>
                          </div>
                          <div className="mt-6 relative" data-id="element-6607">
                            {/* Vertical line */}
                            <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-6608"></div>
                            {/* Steps */}
                            <div className="space-y-8" data-id="element-6609">
                              {workflowInstance.steps.map((step: any, index: number) => <div key={step.id} className="relative flex items-start" data-id="element-6610">
                                    <div className="flex items-center h-6" data-id="element-6611">
                                      <span className={`flex items-center justify-center w-6 h-6 rounded-full ${step.status === 'approved' ? 'bg-green-500 text-white' : step.status === 'rejected' ? 'bg-red-500 text-white' : step.status === 'in_progress' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'} z-10`} data-id="element-6612">
                                        {step.status === 'approved' ? <CheckIcon className="w-3 h-3" data-id="element-6613" /> : step.status === 'rejected' ? <XCircleIcon className="w-3 h-3" data-id="element-6614" /> : step.status === 'in_progress' ? <ClockIcon className="w-3 h-3" data-id="element-6615" /> : index + 1}
                                      </span>
                                    </div>
                                    <div className="ml-4 min-w-0 flex-1" data-id="element-6616">
                                      <div className="text-sm font-medium text-gray-900" data-id="element-6617">
                                        {step.name}
                                      </div>
                                      <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-6618">
                                        <span className="mr-1.5" data-id="element-6619">
                                          Assigned to:
                                        </span>
                                        <span className="font-medium" data-id="element-6620">
                                          {step.assignedRoleName}
                                        </span>
                                      </div>
                                      <div className="mt-1 flex items-center" data-id="element-6621">
                                        {step.status === 'approved' ? <span className="text-xs text-green-600 flex items-center" data-id="element-6622">
                                            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-6623" />
                                            Approved{' '}
                                            {step.timestamp && `on ${formatDate(step.timestamp)}`}
                                          </span> : step.status === 'rejected' ? <span className="text-xs text-red-600 flex items-center" data-id="element-6624">
                                            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-6625" />
                                            Rejected{' '}
                                            {step.timestamp && `on ${formatDate(step.timestamp)}`}
                                          </span> : step.status === 'in_progress' ? <span className="text-xs text-blue-600 flex items-center" data-id="element-6626">
                                            <ClockIcon className="w-3 h-3 mr-1" data-id="element-6627" />
                                            In Progress{' '}
                                            {step.timestamp && `since ${formatDate(step.timestamp)}`}
                                          </span> : <span className="text-xs text-gray-500 flex items-center" data-id="element-6628">
                                            <ClockIcon className="w-3 h-3 mr-1" data-id="element-6629" />
                                            Pending
                                          </span>}
                                      </div>
                                      {step.comments && <div className="mt-2 text-xs italic text-gray-600 bg-gray-50 p-2 rounded-md" data-id="element-6630">
                                          "{step.comments}"
                                        </div>}
                                    </div>
                                  </div>)}
                            </div>
                          </div>
                        </div> : <div className="text-center py-4" data-id="element-6631">
                          <p className="text-gray-500" data-id="element-6632">
                            {application.isPaid ? "Your application is being processed. The approval workflow will be visible once it's assigned." : 'Please complete the payment for your application to initiate the approval process.'}
                          </p>
                          {!application.isPaid && <Button variant="primary" className="mt-4" onClick={() => {}} data-id="element-6633">
                              Make Payment
                            </Button>}
                        </div>}
                    </CardBody>
                  </Card>

                  {/* Application Timeline */}
                  <Card className="mt-6" data-id="element-6634">
                    <CardHeader className="bg-gray-50" data-id="element-6635">
                      <h3 className="text-lg font-medium text-gray-900" data-id="element-6636">
                        Application Timeline
                      </h3>
                    </CardHeader>
                    <CardBody className="p-0" data-id="element-6637">
                      <div className="flow-root" data-id="element-6638">
                        <ul className="divide-y divide-gray-200" data-id="element-6639">
                          {application.timeline && application.timeline.map((event: any, index: number) => <li key={index} className="p-4" data-id="element-6640">
                                  <div className="flex items-start" data-id="element-6641">
                                    <div className="flex-shrink-0" data-id="element-6642">
                                      {event.status === 'submitted' && <FileTextIcon className="h-5 w-5 text-blue-500" data-id="element-6643" />}
                                      {event.status === 'payment_received' && <CreditCardIcon className="h-5 w-5 text-green-500" data-id="element-6644" />}
                                      {event.status === 'approved' && <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-6645" />}
                                      {event.status === 'rejected' && <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-6646" />}
                                      {event.status === 'document_uploaded' && <UploadIcon className="h-5 w-5 text-indigo-500" data-id="element-6647" />}
                                      {event.status === 'comment_added' && <MessageSquareIcon className="h-5 w-5 text-purple-500" data-id="element-6648" />}
                                      {(event.status === 'pending' || event.status === 'processing' || event.status === 'under_review') && <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-6649" />}
                                    </div>
                                    <div className="ml-3 flex-1" data-id="element-6650">
                                      <div className="text-sm text-gray-900 font-medium" data-id="element-6651">
                                        {event.status === 'submitted' && 'Application Submitted'}
                                        {event.status === 'payment_received' && 'Payment Received'}
                                        {event.status === 'approved' && 'Application Approved'}
                                        {event.status === 'rejected' && 'Application Rejected'}
                                        {event.status === 'document_uploaded' && 'Document Uploaded'}
                                        {event.status === 'comment_added' && 'Comment Added'}
                                        {event.status === 'pending' && 'Application Pending'}
                                        {event.status === 'processing' && 'Application Processing'}
                                        {event.status === 'under_review' && 'Under Review'}
                                        {event.status === 'awaiting_payment' && 'Awaiting Payment'}
                                        {event.status === 'awaiting_documents' && 'Awaiting Documents'}
                                      </div>
                                      <div className="mt-1 text-sm text-gray-600" data-id="element-6652">
                                        {event.note}
                                      </div>
                                      <div className="mt-1 text-xs text-gray-500 flex items-center" data-id="element-6653">
                                        <CalendarIcon className="h-3 w-3 mr-1" data-id="element-6654" />
                                        {formatDate(event.timestamp)}
                                        {event.userName && <span className="ml-2 flex items-center" data-id="element-6655">
                                            <UserIcon className="h-3 w-3 mr-1" data-id="element-6656" />
                                            {event.userName}
                                          </span>}
                                      </div>
                                    </div>
                                  </div>
                                </li>)}
                        </ul>
                      </div>
                    </CardBody>
                  </Card>
                </div>

                <div className="space-y-6" data-id="element-6657">
                  {/* Application Details */}
                  <Card data-id="element-6658">
                    <CardHeader className="bg-gray-50" data-id="element-6659">
                      <h3 className="text-lg font-medium text-gray-900" data-id="element-6660">
                        Application Details
                      </h3>
                    </CardHeader>
                    <CardBody className="p-4" data-id="element-6661">
                      <dl className="divide-y divide-gray-200" data-id="element-6662">
                        <div className="py-3 flex justify-between" data-id="element-6663">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-6664">
                            Service
                          </dt>
                          <dd className="text-sm text-gray-900" data-id="element-6665">
                            {application.serviceName}
                          </dd>
                        </div>
                        <div className="py-3 flex justify-between" data-id="element-6666">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-6667">
                            MDA
                          </dt>
                          <dd className="text-sm text-gray-900 flex items-center" data-id="element-6668">
                            <BuildingIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-6669" />
                            {application.mdaName}
                          </dd>
                        </div>
                        <div className="py-3 flex justify-between" data-id="element-6670">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-6671">
                            Applicant
                          </dt>
                          <dd className="text-sm text-gray-900" data-id="element-6672">
                            {application.userName}
                          </dd>
                        </div>
                        <div className="py-3 flex justify-between" data-id="element-6673">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-6674">
                            Submission Date
                          </dt>
                          <dd className="text-sm text-gray-900" data-id="element-6675">
                            {formatDate(application.submittedAt)}
                          </dd>
                        </div>
                        <div className="py-3 flex justify-between" data-id="element-6676">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-6677">
                            Fee
                          </dt>
                          <dd className="text-sm text-gray-900" data-id="element-6678">
                            ₦{application.fee.toLocaleString()}
                          </dd>
                        </div>
                        <div className="py-3 flex justify-between" data-id="element-6679">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-6680">
                            Payment Status
                          </dt>
                          <dd className="text-sm" data-id="element-6681">
                            {application.isPaid ? <span className="text-green-600 flex items-center" data-id="element-6682">
                                <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-6683" />
                                Paid
                              </span> : <span className="text-red-600 flex items-center" data-id="element-6684">
                                <AlertCircleIcon className="h-4 w-4 mr-1" data-id="element-6685" />
                                Unpaid
                              </span>}
                          </dd>
                        </div>
                      </dl>
                    </CardBody>
                  </Card>

                  {/* Documents */}
                  <Card data-id="element-6686">
                    <CardHeader className="bg-gray-50" data-id="element-6687">
                      <h3 className="text-lg font-medium text-gray-900" data-id="element-6688">
                        Documents
                      </h3>
                    </CardHeader>
                    <CardBody className="p-4" data-id="element-6689">
                      {application.documents && application.documents.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-6690">
                          {application.documents.map((doc: any) => <li key={doc.id} className="py-3" data-id="element-6691">
                              <div className="flex items-center justify-between" data-id="element-6692">
                                <div className="flex items-center" data-id="element-6693">
                                  <FileIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-6694" />
                                  <span className="text-sm text-gray-900" data-id="element-6695">
                                    {doc.name}
                                  </span>
                                </div>
                                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-800 text-sm flex items-center" data-id="element-6696">
                                  <DownloadIcon className="h-4 w-4 mr-1" data-id="element-6697" />
                                  Download
                                </a>
                              </div>
                              <div className="mt-1 flex items-center justify-between" data-id="element-6698">
                                <span className="text-xs text-gray-500" data-id="element-6699">
                                  Uploaded on {formatDate(doc.uploadedAt)}
                                </span>
                                {getStatusBadge(doc.status)}
                              </div>
                            </li>)}
                        </ul> : <p className="text-gray-500 text-sm" data-id="element-6700">
                          No documents uploaded yet.
                        </p>}
                      {/* Upload Document Form */}
                      <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6701">
                        <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-6702">
                          Upload New Document
                        </h4>
                        <form onSubmit={handleFileUpload} className="space-y-3" data-id="element-6703">
                          <div data-id="element-6704">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6705">
                              Document Type
                            </label>
                            <select value={uploadType} onChange={e => setUploadType(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" required data-id="element-6706">
                              <option value="" data-id="element-6707">Select document type</option>
                              <option value="identification" data-id="element-6708">
                                Identification
                              </option>
                              <option value="proof_of_address" data-id="element-6709">
                                Proof of Address
                              </option>
                              <option value="application_form" data-id="element-6710">
                                Application Form
                              </option>
                              <option value="supporting_document" data-id="element-6711">
                                Supporting Document
                              </option>
                              <option value="other" data-id="element-6712">Other</option>
                            </select>
                          </div>
                          <div data-id="element-6713">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6714">
                              Select File
                            </label>
                            <div className="mt-1 flex items-center" data-id="element-6715">
                              <label className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none" data-id="element-6716">
                                <span className="flex items-center" data-id="element-6717">
                                  <PaperclipIcon className="h-5 w-5 mr-1" data-id="element-6718" />
                                  {fileUpload ? fileUpload.name : 'Browse files'}
                                </span>
                                <input type="file" className="sr-only" onChange={e => e.target.files && setFileUpload(e.target.files[0])} data-id="element-6719" />
                              </label>
                            </div>
                          </div>
                          <div data-id="element-6720">
                            <Button type="submit" variant="primary" className="w-full flex items-center justify-center" disabled={!fileUpload || !uploadType || submitting} data-id="element-6721">
                              <UploadIcon className="h-4 w-4 mr-2" data-id="element-6722" />
                              {submitting ? 'Uploading...' : 'Upload Document'}
                            </Button>
                          </div>
                        </form>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Comments */}
                  <Card data-id="element-6723">
                    <CardHeader className="bg-gray-50" data-id="element-6724">
                      <h3 className="text-lg font-medium text-gray-900" data-id="element-6725">
                        Comments
                      </h3>
                    </CardHeader>
                    <CardBody className="p-4" data-id="element-6726">
                      {application.comments && application.comments.length > 0 ? <ul className="divide-y divide-gray-200 mb-4" data-id="element-6727">
                          {application.timeline.filter((event: any) => event.status === 'comment_added').map((event: any, index: number) => <li key={index} className="py-3" data-id="element-6728">
                                <div className="flex space-x-3" data-id="element-6729">
                                  <div className="flex-shrink-0" data-id="element-6730">
                                    <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center text-white font-medium" data-id="element-6731">
                                      {event.userName?.charAt(0) || 'U'}
                                    </div>
                                  </div>
                                  <div className="flex-1" data-id="element-6732">
                                    <div className="text-sm font-medium text-gray-900" data-id="element-6733">
                                      {event.userName || 'User'}
                                    </div>
                                    <div className="mt-1 text-sm text-gray-700" data-id="element-6734">
                                      {event.note}
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500" data-id="element-6735">
                                      {formatDate(event.timestamp)}
                                    </div>
                                  </div>
                                </div>
                              </li>)}
                        </ul> : <p className="text-gray-500 text-sm mb-4" data-id="element-6736">
                          No comments yet.
                        </p>}
                      {/* Add Comment Form */}
                      <form onSubmit={handleCommentSubmit} className="mt-4" data-id="element-6737">
                        <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6738">
                          Add a comment
                        </label>
                        <div className="mt-1" data-id="element-6739">
                          <textarea id="comment" name="comment" rows={3} value={comment} onChange={e => setComment(e.target.value)} className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border border-gray-300 rounded-md" placeholder="Enter your comment here..." required data-id="element-6740" />
                        </div>
                        <div className="mt-3 flex justify-end" data-id="element-6741">
                          <Button type="submit" variant="primary" size="sm" className="flex items-center" disabled={!comment.trim() || submitting} data-id="element-6742">
                            <SendIcon className="h-4 w-4 mr-2" data-id="element-6743" />
                            {submitting ? 'Sending...' : 'Send Comment'}
                          </Button>
                        </div>
                      </form>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div> : !loading && !error && <Card data-id="element-6744">
                <CardBody className="p-6 text-center" data-id="element-6745">
                  <p className="text-gray-500" data-id="element-6746">
                    Please enter a reference number to track your application.
                  </p>
                </CardBody>
              </Card>}
        </div>
      </div>
    </UserLayout>;
};
export default TrackApplication;